/**
 * HybridConditionInput — iter760 (Feb 2026)
 *
 * Hybrid smart input for health-condition free-text areas.
 * As the user types, we debounce-call POST /api/health-conditions/parse
 * to detect canonical conditions (fibroadenoma, PCOS, T2D, Hashimoto,
 * BRCA1/2, ...). Detected conditions are shown as chips; clicking
 * "Запази структурирано" upserts them into db.health_conditions via
 * the /bulk endpoint and emits a `conditions-changed` event so BHI /
 * BTRE / BPP / Twin dashboards re-fetch immediately.
 *
 * Backwards compatible: the underlying textarea `value/onChange` stays
 * the source of truth for the parent form (currentConditions), so no
 * consumer needs to change how they persist the free-text field.
 *
 * Props:
 *   - value: string
 *   - onChange: (nextValue: string) => void
 *   - placeholder?: string
 *   - rows?: number
 *   - className?: string       — applied to <textarea>
 *   - bg?: boolean             — currently unused (always shows BG chip labels)
 *   - autoSave?: boolean       — if true, auto-save chips 800 ms after detect
 *   - onSaved?: (savedIds) => void
 *
 * Auth-required for save. Falls back to detect-only if user is anonymous.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Sparkles, X } from "lucide-react";
import { useAuth } from "@/App";
import { emitHealthEvent } from "@/utils/healthEvents";
import { withScoreDelta } from "@/utils/scoreDelta";

const CATEGORY_ACCENT_BG = {
  breast_benign:      { bg: "bg-pink-100",    text: "text-pink-800",    ring: "ring-pink-300" },
  breast_benign_male: { bg: "bg-sky-100",     text: "text-sky-800",     ring: "ring-sky-300"  },
  endocrine:          { bg: "bg-purple-100",  text: "text-purple-800",  ring: "ring-purple-300" },
  metabolic:          { bg: "bg-amber-100",   text: "text-amber-800",   ring: "ring-amber-300" },
  cardio:             { bg: "bg-rose-100",    text: "text-rose-800",    ring: "ring-rose-300"  },
  autoimmune:         { bg: "bg-orange-100",  text: "text-orange-800",  ring: "ring-orange-300" },
  inflammation:       { bg: "bg-red-100",     text: "text-red-800",     ring: "ring-red-300" },
  nutrition:          { bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300" },
  genetic:            { bg: "bg-indigo-100",  text: "text-indigo-800",  ring: "ring-indigo-300" },
  other:              { bg: "bg-slate-100",   text: "text-slate-800",   ring: "ring-slate-300" },
};

export default function HybridConditionInput({
  value = "",
  onChange,
  placeholder = "Опишете със свои думи или натиснете „Открий състояния\" за да разпознаем каноничните диагнози...",
  rows = 3,
  className = "",
  autoSave = false,
  onSaved,
}) {
  const { API, user } = useAuth();
  const [detected, setDetected] = useState([]);         // [{condition_id, name_bg, category, keyword_matched}]
  const [dismissed, setDismissed] = useState(new Set()); // user-rejected suggestions in this session
  const [saved, setSaved] = useState(new Set());        // condition_ids the user already saved
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced detector — auth optional (parse endpoint is public).
  const scan = useCallback(async (txt) => {
    if (!txt || !txt.trim()) { setDetected([]); return; }
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setScanning(true);
    try {
      const { data } = await axios.post(
        `${API}/health-conditions/parse`,
        { text: txt },
        { signal: ac.signal }
      );
      setDetected(data?.detected || []);
    } catch (e) {
      if (e?.code !== "ERR_CANCELED") setDetected([]);
    } finally {
      setScanning(false);
    }
  }, [API]);

  // Debounce user typing (500 ms)
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => scan(value), 500);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, scan]);

  const pending = useMemo(
    () => detected.filter((d) => !dismissed.has(d.condition_id) && !saved.has(d.condition_id)),
    [detected, dismissed, saved]
  );

  const saveAll = useCallback(async () => {
    if (!user) {
      setMsg("Влезте в акаунта си, за да запишете състоянията структурирано.");
      return;
    }
    if (pending.length === 0) return;
    setSaving(true);
    setMsg(null);
    try {
      const items = pending.map((d) => ({ condition_id: d.condition_id, severity: "self_reported" }));
      // iter761 — capture before/after BHI + Twin snapshot to show real impact toast.
      const { actionResult, delta } = await withScoreDelta(
        API,
        () => axios.post(`${API}/health-conditions/bulk`, { items }),
        { kind: "add" }
      );
      const data = actionResult?.data;
      const newlySaved = new Set(saved);
      (data?.saved || []).forEach((s) => newlySaved.add(s.condition_id));
      setSaved(newlySaved);
      setMsg(
        delta && !delta.unchanged
          ? `Запазени: ${data?.count || items.length}. ${delta.message}`
          : `Запазени: ${data?.count || items.length}. BHI и Twins ще се преизчислят при следващо обновяване.`
      );
      emitHealthEvent("conditions-changed", { added: items.map((i) => i.condition_id), delta });
      onSaved?.(Array.from(newlySaved));
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Грешка при запис.");
    } finally {
      setSaving(false);
    }
  }, [API, user, pending, saved, onSaved]);

  // Optional auto-save (used from onboarding wizard where the user cannot save until logged-in)
  useEffect(() => {
    if (!autoSave || !user || pending.length === 0 || saving) return;
    const t = setTimeout(() => { saveAll(); }, 800);
    return () => clearTimeout(t);
  }, [autoSave, user, pending, saving, saveAll]);

  const dismiss = (cid) => {
    const next = new Set(dismissed);
    next.add(cid);
    setDismissed(next);
  };

  return (
    <div className="space-y-2" data-testid="hybrid-condition-input">
      <textarea
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid="hybrid-condition-textarea"
        className={className || "w-full rounded-xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm"}
      />

      {/* Detector strip */}
      <div className="flex items-center gap-2 text-[11px] text-slate-600" data-testid="hybrid-condition-detector-strip">
        {scanning ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-600" />
            <span>Разпознаваме канонични диагнози...</span>
          </>
        ) : detected.length === 0 ? (
          <>
            <Sparkles className="w-3.5 h-3.5 text-violet-500" />
            <span>Пишете свободно — ще открием ключови състояния (PCOS, Хашимото, T2D, BRCA1/2, фиброаденом, ...) и ще предложим да ги запазим структурирано.</span>
          </>
        ) : (
          <>
            <Sparkles className="w-3.5 h-3.5 text-violet-600" />
            <span data-testid="detected-count">Открити <b>{detected.length}</b> канонични състояния:</span>
          </>
        )}
      </div>

      {/* Chips */}
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="hybrid-chip-row">
          {pending.map((d) => {
            const c = CATEGORY_ACCENT_BG[d.category] || CATEGORY_ACCENT_BG.other;
            return (
              <span
                key={d.condition_id}
                data-testid={`hybrid-chip-${d.condition_id}`}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold ring-1 ${c.bg} ${c.text} ${c.ring}`}
              >
                <CheckCircle2 className="w-3 h-3" />
                {d.name_bg}
                <button
                  type="button"
                  onClick={() => dismiss(d.condition_id)}
                  className="hover:bg-white/50 rounded p-0.5"
                  aria-label="Игнорирай"
                  data-testid={`hybrid-chip-dismiss-${d.condition_id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          {user && (
            <button
              type="button"
              onClick={saveAll}
              disabled={saving}
              data-testid="hybrid-save-all-btn"
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold text-white ${
                saving ? "bg-slate-400 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-700"
              }`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Запази структурирано ({pending.length})
            </button>
          )}
        </div>
      )}

      {/* Saved chip row */}
      {saved.size > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="hybrid-saved-row">
          <span className="text-emerald-700 font-semibold">✓ Запазени:</span>
          {Array.from(saved).map((cid) => {
            const d = detected.find((x) => x.condition_id === cid);
            return (
              <span
                key={cid}
                data-testid={`hybrid-saved-chip-${cid}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300"
              >
                <CheckCircle2 className="w-3 h-3" />
                {d?.name_bg || cid}
              </span>
            );
          })}
        </div>
      )}

      {msg && (
        <div className="text-[11px] text-slate-600" data-testid="hybrid-msg">
          {msg}
        </div>
      )}
    </div>
  );
}
